import { apiClient } from "@/api/client";
import type { Paginated } from "@/types/common";
import type { SupportSummary, SupportTicket, TicketMessage, TicketPriority, TicketSource, TicketStatus } from "@/types/domain";

export interface TicketListQuery {
  page?: number;
  limit?: number;
  /** `OPEN` = Unresolved, `WAITING` = In progress, `RESOLVED` = Resolved; or a comma list of them (`OPEN,WAITING`). */
  status?: TicketStatus | string;
  sos?: boolean;
  /** One category, a comma-separated list, or `NONE` (no topic, not SOS). */
  category?: string;
  /** Who raised the ticket. */
  source?: TicketSource;
  priority?: TicketPriority;
}

/** Talks to admin-service's support delegate (ops.service.ts listTickets/getTicket/replyToTicket/updateTicket). */
export const supportApi = {
  /** GET /admin/support/summary — ticket counts per topic and status. */
  summary: () => apiClient.get<SupportSummary>("/admin/support/summary").then((r) => r.data),

  /** GET /admin/support/tickets — SOS first, then priority, newest first. */
  list: (query: TicketListQuery) =>
    apiClient
      .get<Paginated<SupportTicket>>("/admin/support/tickets", {
        params: { ...query, sos: query.sos ? "true" : undefined },
      })
      .then((r) => r.data),

  /** GET /admin/support/tickets/:id — with the full message thread. */
  getById: (id: string) => apiClient.get<SupportTicket>(`/admin/support/tickets/${id}`).then((r) => r.data),

  /** POST /admin/support/tickets/:id/messages — delivered live to the customer's app and emailed to the address on the ticket. */
  reply: (id: string, text: string) =>
    apiClient
      .post<{ ticketId: string; message: TicketMessage; status: TicketStatus; emailedTo: string | null }>(`/admin/support/tickets/${id}/messages`, { text })
      .then((r) => r.data),

  /** PATCH /admin/support/tickets/:id — resolving also emails the customer the resolution (`emailedTo` = where). */
  update: (id: string, payload: { status?: TicketStatus; priority?: TicketPriority; resolution?: string }) =>
    apiClient.patch<SupportTicket & { emailedTo?: string | null }>(`/admin/support/tickets/${id}`, payload).then((r) => r.data),
};
